// ============================================================
// types/matricula.types.ts
// ============================================================

// ── Estadísticas de matrículas ───────────────────────────────
// GET /matriculas/estadisticas?anioEscolarId={id}

export interface EstadisticasNivel {
  nivel:                        string;
  totalMatriculas:              number;
  pagosMatriculaPendientesCount: number;
  pagosMatriculaPendientesMonto: number;
  pensionesPendientesCount:     number;
  pensionesPendientesMonto:     number;
  totalACobrar:                 number;
}

export interface MatriculaEstadisticas {
  anioEscolarId:                number;
  anio:                         number;
  totalMatriculas:              number;
  capacidadTotal:               number;
  pagosMatriculaPendientesCount: number;
  pagosMatriculaPendientesMonto: number;
  pensionesPendientesCount:     number;
  pensionesPendientesMonto:     number;
  totalACobrar:                 number;
  porNivel:                     EstadisticasNivel[];
}

// ── Body exacto → POST /matriculas ───────────────────────────
export interface MatriculaPayload {
  estudianteId:  number;
  gradoId:       number;
  seccionId:     number;
  anioEscolarId: number;
}

// ── Body → PUT /matriculas/{id}/ajustar-tarifas ───────────────
export interface AjustarTarifasPayload {
  costoMatricula: number | null;
  costoPension:   number | null;
}

// ── Resumen de pago de matrícula embebido en MatriculaResponse ─
export interface MatriculaPagoInfo {
  idPagoMatricula: number;
  montoTarifa:     number;
  montoDescuento:  number;
  montoFinal:      number;
  montoPagado:     number | null;
  estado:          'PENDIENTE' | 'PAGADO' | 'ANULADO';
  fechaPago:       string | null;
}

// ── Resumen de pensiones embebido en MatriculaResponse ─────────
export interface MatriculaPensionesResumen {
  total:              number;
  pagadas:            number;
  pendientes:         number;
  anuladas:           number;
  montoMensual:       number;
  montoTotalTarifa:   number;
  montoTotalPagado:   number;
}

// ── Response del backend (data es un array) ───────────────────
export interface MatriculaResponse {
  idMatricula:          number;
  codigoMatricula:      string | null;
  fase:                 'REGULAR' | 'RECUPERACION';
  estudianteId:         number;
  estudianteNombres:    string;
  estudianteApellidos:  string;
  estudianteDni:        string;
  estudianteCodigoSiagie: string | null;
  gradoId:              number;
  gradoNombre:          string;
  gradoNivel:           string;
  seccionId:            number;
  seccionNombre:        string;
  anioEscolarId:        number;
  anio:                 number;
  fechaMatricula:       string | null;
  activo:               boolean;
  pagoMatricula:        MatriculaPagoInfo | null;
  pensiones:            MatriculaPensionesResumen | null;
}

// ── Body exacto → POST /asignaciones-docente ─────────────────
export interface AsignacionPayload {
  docenteId:     number;
  cursoId:       number;
  gradoId:       number;
  seccionId:     number;
  anioEscolarId: number;
  orden?:        number;   // posición CNEB en la boleta; omitir = queda en 99 (al final)
}

// ── Response del backend (data es un array) ───────────────────
export interface AsignacionItem {
  idAsignacion:      number;
  docenteId:         number;
  docenteNombres:    string;
  docenteApellidos:  string;
  cursoId:           number;
  cursoNombre:       string;
  gradoId:           number;
  gradoNombre:       string;
  gradoNivel:        string;
  seccionId:         number;
  seccionNombre:     string;
  anioEscolarId:     number;
  anio:              number;
  orden:             number;   // posición CNEB; 99 = sin configurar (al final)
  activo:            boolean;
}

// ── Response de cursos del estudiante ────────────────────────
export interface CursoEstudianteItem {
  asignacionAcademicaId: number;
  cursoId:               number;
  cursoNombre:           string;
  docenteId:             number;
  docenteNombres:        string;
  docenteApellidos:      string;
}

export interface CursosEstudianteResponse {
  estudianteId:        number;
  estudianteNombres:   string;
  estudianteApellidos: string;
  gradoNombre:         string;
  seccionNombre:       string;
  anio:                number;
  cursos:              CursoEstudianteItem[];
}

// ── Estado interno formulario matrícula ───────────────────────
export interface MatriculaFormState {
  anioEscolarId: string;
  gradoId:       string;
  seccionId:     string;
}

export const MATRICULA_INITIAL: MatriculaFormState = {
  anioEscolarId: '',
  gradoId:       '',
  seccionId:     '',
};

// ── Matrícula Masiva ──────────────────────────────────────────
export interface MatriculaMasivaItem {
  dni:          string;
  gradoNombre:  string;
  nivel:        string;
  seccionNombre:string;
}

export interface MatriculaMasivaPayload {
  anio:       number;
  matriculas: MatriculaMasivaItem[];
}

export interface MatriculaMasivaError {
  fila:   number;
  dni:    string;
  motivo: string;
}

export interface MatriculaMasivaResponse {
  totalEnviados: number;
  totalCreados:  number;
  totalErrores:  number;
  creados:       MatriculaResponse[];
  errores:       MatriculaMasivaError[];
}

// ── Estado interno formulario asignación ─────────────────────
export interface AsignacionFormState {
  anioEscolarId: string;
  gradoId:       string;
  seccionId:     string;
  cursosIds:     number[];
}

export const ASIGNACION_INITIAL: AsignacionFormState = {
  anioEscolarId: '',
  gradoId:       '',
  seccionId:     '',
  cursosIds:     [],
};